import { BOOKING_ERROR_CODES, TOO_MANY_ATTEMPTS_MESSAGE } from '@shared/core/bookings'
import { extractErrorMessage } from '@shared/util'
import { addMonths, format, startOfDay, subMonths } from 'date-fns'
import ms from 'ms'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getTimezoneOptions } from 'src/helpers/timezones'
import { useAppointmentTypes, useComputeSlotsForCustomerDay, useProviderDetails } from 'src/modules/booking/hooks'
import { isCalendarRefusedError } from 'src/modules/booking/utils/calendar-refused'
import { isTooManyRequestsError } from 'src/modules/booking/utils/too-many-requests'
import {
  availabilityErrorMessage,
  buildAppointmentTypeOptions,
  buildCalendarDayViews,
  calendarRefusedMessage,
  canNavigateToNextMonth,
  canNavigateToPrevMonth,
  DAY_SLOTS_ERROR_MESSAGE,
  FIRST_AVAILABLE_DATE_ERROR_MESSAGE,
  formatSlotsForDisplay,
  getContinueButtonLabel,
  parseFirstAvailableDate,
  resolveInitialAppointmentType,
  resolveInitialTimezone,
  resolveLastBookableDay,
  resolveSelectedCancellationPolicy,
  shouldShowAppointmentTypeSelector,
  validateContinueAndBuildPayload,
} from './booking-date-and-time-step.domain'
import type { BookingDateAndTimeStepProps } from './booking-date-and-time-step.interface'

const TIMEZONE_STORAGE_KEY = 'booking-timezone-preference'
const RESCHEDULE_FAILURE_MESSAGES: Record<string, string> = {
  reschedule_calendar_refused: "We couldn't move your appointment. Please contact the business directly to reschedule.",
  reschedule_calendar_unavailable: 'Google Calendar is busy right now. Please try again in a minute.',
  [BOOKING_ERROR_CODES.tooManyRequests]: TOO_MANY_ATTEMPTS_MESSAGE,
  policy_window:
    "This appointment is inside the business's cancellation window, so it can't be moved online. Please contact them to change the time.",
}

export function useBookingDateAndTimeStep({
  providerId,
  onNext,
  initialData,
  isRescheduling = false,
  allowChangingAppointmentType,
  appointmentTypes: appointmentTypesFromProps,
  rescheduleCode,
}: BookingDateAndTimeStepProps) {
  const providerIdOrEmpty = providerId ?? ''
  const { data: providerDetails } = useProviderDetails(providerIdOrEmpty, { enabled: !!providerId })
  const providerName = providerDetails?.full_name ?? 'Provider'

  const timezoneOptions = useMemo(() => getTimezoneOptions(), [])
  const initialTimezone = useMemo(
    () => resolveInitialTimezone(timezoneOptions, getTimezoneSources(initialData?.timezone)),
    [timezoneOptions, initialData?.timezone],
  )

  const useRescheduleTypes = isRescheduling && appointmentTypesFromProps != null && appointmentTypesFromProps.length > 0
  const { data: fetchedAppointmentTypes = [], isLoading: isLoadingAppointmentTypes } = useAppointmentTypes(
    providerIdOrEmpty,
    { enabled: !!providerId && !useRescheduleTypes },
  )
  const appointmentTypes = useRescheduleTypes ? appointmentTypesFromProps : fetchedAppointmentTypes

  const initialAppointmentType = useMemo(
    () => resolveInitialAppointmentType(appointmentTypes, initialData?.appointmentType),
    [appointmentTypes, initialData?.appointmentType],
  )

  const [selectedAppointmentType, setSelectedAppointmentType] = useState(initialAppointmentType)
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialData?.selectedDate ?? null)
  const [selectedTime, setSelectedTime] = useState<string | null>(initialData?.selectedTime ?? null)
  const [currentMonth, setCurrentMonth] = useState(() => new Date())
  const [timezone, setTimezone] = useState(initialTimezone)
  const [slotAvailabilityError, setSlotAvailabilityError] = useState<string | null>(null)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)
  const [refusedAppointmentTypeId, setRefusedAppointmentTypeId] = useState<string | null>(null)
  // Refetching after a 429 only feeds the limit; a retry click or a new date resumes it.
  const [isRateLimited, setIsRateLimited] = useState(false)
  // Availability reads the appointment type's calendar, so a refusal is scoped to that type.
  const isRefusalRecorded = !!selectedAppointmentType && refusedAppointmentTypeId === selectedAppointmentType

  useEffect(() => {
    try {
      localStorage.setItem(TIMEZONE_STORAGE_KEY, timezone)
    } catch {
      // ignore
    }
  }, [timezone])

  useEffect(() => {
    if (selectedAppointmentType) return

    if (isRescheduling) {
      initialData?.appointmentType && setSelectedAppointmentType(initialData.appointmentType)
      return
    }

    const defaultOrFirstAppointmentTypeId =
      appointmentTypes.find((appointmentType) => appointmentType.is_default)?.id ?? appointmentTypes[0]?.id
    if (defaultOrFirstAppointmentTypeId) {
      setSelectedAppointmentType(defaultOrFirstAppointmentTypeId)
    }
  }, [isRescheduling, initialData?.appointmentType, appointmentTypes, selectedAppointmentType])

  const {
    data: firstAvailableDateData,
    isLoading: isLoadingFirstDate,
    isFetching: isFetchingFirstDate,
    error: firstAvailableDateError,
    refetch: refetchFirstAvailableDate,
  } = useComputeSlotsForCustomerDay(
    {
      providerId: providerIdOrEmpty,
      appointmentTypeId: selectedAppointmentType,
      customerTz: timezone,
      rescheduleCode,
    },
    {
      enabled: !selectedDate && !!providerId && !!selectedAppointmentType && !isRefusalRecorded,
      staleTime: ms('5 minutes'),
      refetchOnWindowFocus: !isRateLimited,
    },
  )

  useEffect(() => {
    if (!firstAvailableDateData?.summary || selectedDate || !selectedAppointmentType) return
    const date = parseFirstAvailableDate(firstAvailableDateData.summary)
    if (date) setSelectedDate(date)
  }, [firstAvailableDateData, selectedDate, selectedAppointmentType])

  const customerDateISO = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''
  const {
    data: slotsData,
    isLoading: isLoadingSlots,
    isFetching: isFetchingSlots,
    error: slotsError,
    refetch: refetchSlots,
  } = useComputeSlotsForCustomerDay(
    {
      providerId: providerIdOrEmpty,
      appointmentTypeId: selectedAppointmentType,
      customerDateISO,
      customerTz: timezone,
      rescheduleCode,
    },
    {
      enabled: !!selectedDate && !!providerId && !!selectedAppointmentType && !isRefusalRecorded,
      refetchInterval: (_data, query) => (stopsPolling(query.state.error) ? false : 30000),
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: !isRateLimited,
      staleTime: ms('5 seconds'),
      cacheTime: ms('5 minutes'),
    },
  )

  const hasRefusedError = isCalendarRefusedError(firstAvailableDateError) || isCalendarRefusedError(slotsError)
  useEffect(() => {
    if (hasRefusedError && selectedAppointmentType) setRefusedAppointmentTypeId(selectedAppointmentType)
  }, [hasRefusedError, selectedAppointmentType])

  const hasRateLimitedError = isTooManyRequestsError(firstAvailableDateError) || isTooManyRequestsError(slotsError)
  useEffect(() => {
    setIsRateLimited(hasRateLimitedError)
  }, [hasRateLimitedError])

  useEffect(() => {
    if (!isRescheduling) {
      setSelectedDate(null)
    }
  }, [selectedAppointmentType, isRescheduling])

  useEffect(() => {
    setSelectedTime(null)
    setSlotAvailabilityError(null)
  }, [selectedDate, timezone])

  useEffect(() => {
    if (selectedDate) {
      setSelectedTime(null)
      setSlotAvailabilityError(null)
    }
  }, [selectedAppointmentType, selectedDate])

  const today = useMemo(() => startOfDay(new Date()), [])
  const appointmentTypeOptions = useMemo(() => buildAppointmentTypeOptions(appointmentTypes), [appointmentTypes])
  const canNavigateToPrevMonthFlag = useMemo(() => canNavigateToPrevMonth(currentMonth, today), [currentMonth, today])
  // The first-available response is what the picker holds before any date is
  // picked, so it is what bounds the very first paint.
  const lastBookableDay = useMemo(
    () =>
      resolveLastBookableDay(
        slotsData ? slotsData.booking_window_end_date : firstAvailableDateData?.booking_window_end_date,
      ),
    [slotsData, firstAvailableDateData?.booking_window_end_date],
  )
  const canNavigateToNextMonthFlag = useMemo(
    () => canNavigateToNextMonth(currentMonth, lastBookableDay),
    [currentMonth, lastBookableDay],
  )
  const calendarDayViews = useMemo(
    () => buildCalendarDayViews(currentMonth, selectedDate, today, lastBookableDay),
    [currentMonth, selectedDate, today, lastBookableDay],
  )
  const availableTimeSlots = useMemo(
    () => formatSlotsForDisplay(slotsData?.slots ?? [], timezone),
    [slotsData?.slots, timezone],
  )
  const selectedAppointmentTypeLabel = useMemo(
    () => appointmentTypeOptions.find((o) => o.value === selectedAppointmentType)?.label ?? undefined,
    [appointmentTypeOptions, selectedAppointmentType],
  )

  const cancellationPolicy = useMemo(
    () => resolveSelectedCancellationPolicy(fetchedAppointmentTypes, selectedAppointmentType, isRescheduling),
    [fetchedAppointmentTypes, selectedAppointmentType, isRescheduling],
  )

  const showAppointmentTypeSelector = shouldShowAppointmentTypeSelector(isRescheduling, allowChangingAppointmentType)
  const appointmentTypeLabelForDisplay = selectedAppointmentTypeLabel ?? 'Loading...'
  const continueButtonLabel = getContinueButtonLabel(isRescheduling)

  const handlePrevMonth = useCallback(() => {
    if (canNavigateToPrevMonth(currentMonth, startOfDay(new Date()))) {
      setCurrentMonth(subMonths(currentMonth, 1))
    }
  }, [currentMonth])

  const handleNextMonth = useCallback(() => {
    if (canNavigateToNextMonth(currentMonth, lastBookableDay)) {
      setCurrentMonth(addMonths(currentMonth, 1))
    }
  }, [currentMonth, lastBookableDay])

  const handleDateSelect = useCallback(
    (date: Date) => {
      if (!selectedAppointmentType) return
      setSelectedDate(date)
      setSelectedTime(null)
    },
    [selectedAppointmentType],
  )

  const handleUseBrowserTimezone = useCallback(() => {
    try {
      const browser = Intl.DateTimeFormat().resolvedOptions().timeZone
      setTimezone(browser)
    } catch {
      // ignore
    }
  }, [])

  const handleTimeSelect = useCallback((time: string) => {
    setSelectedTime(time)
    setSlotAvailabilityError(null)
  }, [])

  const handleRetryFirstAvailableDate = useCallback(() => {
    refetchFirstAvailableDate()
  }, [refetchFirstAvailableDate])

  const handleRetrySlots = useCallback(() => {
    refetchSlots()
  }, [refetchSlots])

  const handleContinue = useCallback(async () => {
    if (!selectedDate || !selectedTime) return

    setIsCheckingAvailability(true)
    setSlotAvailabilityError(null)

    if (!slotsData?.slots) {
      setSlotAvailabilityError('Slot data is not available. Please try again.')
      setIsCheckingAvailability(false)
      return
    }

    try {
      // A failed refetch in react-query v3 still carries the previous data, so the error must win.
      const { data: freshSlotsData, error: freshSlotsError, isError } = await refetchSlots()
      if (isCalendarRefusedError(freshSlotsError)) setRefusedAppointmentTypeId(selectedAppointmentType)

      if (isError || !freshSlotsData?.slots) {
        setSlotAvailabilityError(
          isTooManyRequestsError(freshSlotsError)
            ? TOO_MANY_ATTEMPTS_MESSAGE
            : 'Unable to verify slot availability. Please try again.',
        )
        setIsCheckingAvailability(false)
        return
      }

      const result = validateContinueAndBuildPayload(
        freshSlotsData.slots,
        selectedTime,
        timezone,
        selectedAppointmentType,
        selectedDate,
      )

      if (result.ok) {
        try {
          await Promise.resolve(onNext(result.payload))
        } catch (e) {
          setSlotAvailabilityError(resolveContinueErrorMessage(e))
        }
        setIsCheckingAvailability(false)
      } else {
        const errorMsg = 'error' in result ? result.error : ''
        setSlotAvailabilityError(errorMsg)
        setSelectedTime(null)
        if (errorMsg.includes('no longer available')) {
          refetchSlots()
        }
        setIsCheckingAvailability(false)
      }
    } catch {
      setSlotAvailabilityError('Unable to verify slot availability. Please try again.')
      setIsCheckingAvailability(false)
    }
  }, [selectedDate, selectedTime, slotsData?.slots, timezone, selectedAppointmentType, refetchSlots, onNext])

  return {
    providerName,
    timezoneOptions,
    appointmentTypeOptions,
    selectedAppointmentType,
    setSelectedAppointmentType,
    selectedDate,
    selectedTime,
    currentMonth,
    timezone,
    setTimezone,
    calendarDayViews,
    canNavigateToPrevMonth: canNavigateToPrevMonthFlag,
    canNavigateToNextMonth: canNavigateToNextMonthFlag,
    availableTimeSlots,
    selectedAppointmentTypeLabel,
    slotAvailabilityError,
    isCheckingAvailability,
    isLoadingAppointmentTypes,
    isLoadingFirstDate,
    isLoadingSlots,
    slotsError,
    slotsErrorMessage: availabilityErrorMessage(slotsError, DAY_SLOTS_ERROR_MESSAGE),
    firstAvailableDateError,
    firstAvailableDateErrorMessage: availabilityErrorMessage(
      firstAvailableDateError,
      FIRST_AVAILABLE_DATE_ERROR_MESSAGE,
    ),
    isCalendarRefused: isRefusalRecorded || hasRefusedError,
    calendarRefusedMessage: calendarRefusedMessage(isRescheduling),
    isRetryingFirstAvailableDate: isFetchingFirstDate,
    isRetryingSlots: isFetchingSlots,
    hasSelectedAppointmentType: !!selectedAppointmentType,
    cancellationPolicy,
    hasSelectedDate: !!selectedDate,
    showAppointmentTypeSelector,
    appointmentTypeLabelForDisplay,
    continueButtonLabel,
    handlePrevMonth,
    handleNextMonth,
    handleDateSelect,
    handleUseBrowserTimezone,
    handleTimeSelect,
    handleContinue,
    handleRetryFirstAvailableDate,
    handleRetrySlots,
  }
}

function stopsPolling(error: unknown): boolean {
  return isCalendarRefusedError(error) || isTooManyRequestsError(error)
}

function resolveContinueErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { code?: unknown; error?: unknown } } })?.response?.data
  return (
    knownRescheduleFailureMessage(data?.code) ??
    knownRescheduleFailureMessage(data?.error) ??
    extractErrorMessage(error)
  )
}

function knownRescheduleFailureMessage(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  return Object.prototype.hasOwnProperty.call(RESCHEDULE_FAILURE_MESSAGES, value)
    ? RESCHEDULE_FAILURE_MESSAGES[value]
    : undefined
}

function getTimezoneSources(initialTimezone?: string): {
  saved?: string
  initial?: string
  browser?: string
} {
  let saved: string | undefined
  let browser: string | undefined
  try {
    saved = localStorage.getItem(TIMEZONE_STORAGE_KEY) ?? undefined
  } catch {
    saved = undefined
  }
  try {
    browser = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    browser = undefined
  }
  return { saved, initial: initialTimezone, browser }
}
